import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AvailabilityExceptionParams,
  Contractor,
  ContractorAvailabilityException,
  ContractorDocument,
  ContractorDocumentPayload,
  ContractorPayload,
  ContractorScheduleDto,
  ContractorAvailabilityDto,
  CreateContractorAvailability,
  CreateContractorAvailabilityException,
  UpdateContractorAvailability,
  UpdateContractorAvailabilityException,
  UpdateContractorDto
} from '@umss/shared/data-access';
import { Observable, Subject, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ContractorService {
  private readonly apiUrl = '/api/Contractors';

  readonly availabilityUpdated$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  get(id: string): Observable<Contractor> {
    return this.http.get<Contractor>(`${this.apiUrl}/${id}`);
  }

  create(payload: Partial<ContractorPayload>): Observable<Contractor> {
    return this.http.post<Contractor>(`${this.apiUrl}`, payload);
  }

  update(id: string, payload: Partial<Contractor>): Observable<Contractor> {
    return this.http.put<Contractor>(`${this.apiUrl}/${id}`, payload);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // Availability

  getAvailability(id: string): Observable<ContractorAvailabilityDto[]> {
    return this.http.get<ContractorAvailabilityDto[]>(
      `${this.apiUrl}/${id}/availability`
    );
  }

  createAvailability(
    id: string,
    availability: CreateContractorAvailability
  ): Observable<string[]> {
    return this.http.post<string[]>(
      `${this.apiUrl}/${id}/availability`,
      availability
    );
  }

  updateAvailability(
    id: string,
    availabilityId: string,
    availability: UpdateContractorAvailability
  ): Observable<ContractorAvailabilityDto> {
    {
      return this.http.put<ContractorAvailabilityDto>(
        `${this.apiUrl}/${id}/availability/${availabilityId}`,
        availability
      );
    }
  }

  deleteAvailability(id: string, availabilityId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${id}/availability/${availabilityId}`
    );
  }

  // Documents
  getDocuments(id: string): Observable<ContractorDocument[]> {
    return this.http.get<ContractorDocument[]>(
      `${this.apiUrl}/${id}/documents`
    );
  }

  createContractorDocument(
    id: string,
    payload: ContractorDocumentPayload
  ): Observable<ContractorDocument> {
    return this.http.post<ContractorDocument>(
      `${this.apiUrl}/${id}/documents`,
      payload
    );
  }

  verifyContractorDocument(documentId: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/documents/${documentId}/verify`,
      {}
    );
  }

  // me

  getMe(): Observable<Contractor> {
    return this.http.get<Contractor>(`${this.apiUrl}/me`);
  }

  updateMe(payload: Partial<UpdateContractorDto>): Observable<Contractor> {
    return this.http.put<Contractor>(`${this.apiUrl}/me`, payload);
  }

  // Me - Availability (self-service endpoints for contractors)

  getMyAvailability(): Observable<ContractorAvailabilityDto[]> {
    return this.http.get<ContractorAvailabilityDto[]>(
      `${this.apiUrl}/me/availability`
    );
  }

  createMyAvailability(
    availability: CreateContractorAvailability
  ): Observable<string[]> {
    return this.http
      .post<string[]>(`${this.apiUrl}/me/availability`, availability)
      .pipe(tap(() => this.availabilityUpdated$.next()));
  }

  updateMyAvailability(
    availabilityId: string,
    availability: UpdateContractorAvailability
  ): Observable<ContractorAvailabilityDto> {
    return this.http
      .put<ContractorAvailabilityDto>(
        `${this.apiUrl}/me/availability/${availabilityId}`,
        availability
      )
      .pipe(tap(() => this.availabilityUpdated$.next()));
  }

  deleteMyAvailability(availabilityId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/me/availability/${availabilityId}`)
      .pipe(tap(() => this.availabilityUpdated$.next()));
  }

  // Me - Availability Exceptions

  getMyAvailabilityExceptions(
    params?: AvailabilityExceptionParams
  ): Observable<ContractorAvailabilityException[]> {
    let httpParams = new HttpParams();

    if (params) {
      httpParams = httpParams
        .append('startDate', params.startDate)
        .append('endDate', params.endDate);
    }

    return this.http.get<ContractorAvailabilityException[]>(
      `${this.apiUrl}/me/availability-exceptions`,
      { params: httpParams }
    );
  }

  createMyAvailabilityException(
    exception: CreateContractorAvailabilityException
  ): Observable<ContractorAvailabilityException> {
    return this.http.post<ContractorAvailabilityException>(
      `${this.apiUrl}/me/availability-exceptions`,
      exception
    );
  }

  updateMyAvailabilityException(
    exceptionId: number,
    exception: UpdateContractorAvailabilityException
  ): Observable<ContractorAvailabilityException> {
    return this.http.put<ContractorAvailabilityException>(
      `${this.apiUrl}/me/availability-exceptions/${exceptionId}`,
      exception
    );
  }

  deleteMyAvailabilityException(exceptionId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/me/availability-exceptions/${exceptionId}`
    );
  }

  // Me - Schedule (combined weekly + exceptions)

  getMySchedule(
    params: AvailabilityExceptionParams
  ): Observable<ContractorScheduleDto> {
    const httpParams = new HttpParams()
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);
    return this.http.get<ContractorScheduleDto>(`${this.apiUrl}/me/schedule`, {
      params: httpParams
    });
  }

  // Me - Documents (self-service endpoints for contractors)

  getMyDocuments(): Observable<ContractorDocument[]> {
    return this.http.get<ContractorDocument[]>(`${this.apiUrl}/me/documents`);
  }

  createMyDocument(
    payload: ContractorDocumentPayload
  ): Observable<ContractorDocument> {
    return this.http.post<ContractorDocument>(
      `${this.apiUrl}/me/documents`,
      payload
    );
  }
}
